import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@nextui-org/react'
import { FC, useCallback, useEffect, useState } from 'react'
import { MdDownload, MdSync } from 'react-icons/md'
import { useSnackbar } from 'notistack'
import { BoxLabelRow, labelsApi, resolveLabelUrl } from '../api'

/**
 * Lista las etiquetas de todas las cajas (pares de sellos) de una recolección y
 * permite descargarlas. El botón "Sincronizar" crea las etiquetas de las cajas
 * recién selladas (POST /labels/syncBoxLabels) y refresca la lista.
 */
const CajasModal: FC<{
  target: { id_collection: number, id_store: number, store: string } | null
  idSeller: number | null
  userId: number | null
  onClose: () => void
}> = ({ target, idSeller, userId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState<BoxLabelRow[]>([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const cargar = useCallback(async () => {
    if (target === null) return
    setLoading(true)
    try {
      setRows(await labelsApi.boxLabels(target.id_collection, idSeller))
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudieron cargar las cajas', { variant: 'error' })
    } finally { setLoading(false) }
  }, [target, idSeller, enqueueSnackbar])

  // Al abrir solo lista las etiquetas. La generación ocurre al sellar (sendToCustody);
  // el botón "Sincronizar cajas" queda como refuerzo manual.
  useEffect(() => { void cargar() }, [cargar])

  if (target === null) return null

  const sincronizar = async (): Promise<void> => {
    setSyncing(true)
    try {
      const r = await labelsApi.syncBoxLabels(target.id_collection, target.id_store, idSeller, userId)
      enqueueSnackbar(r.etiquetas_creadas > 0 ? `${r.etiquetas_creadas} etiqueta(s) creada(s)` : 'Sin cajas nuevas por etiquetar', { variant: 'success' })
      await cargar()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudieron sincronizar las cajas', { variant: 'error' })
    } finally { setSyncing(false) }
  }

  const sellosTxt = (b: BoxLabelRow): string =>
    b.seal !== null ? `Sellos ${b.seal}${b.seal2 !== null ? ` / ${b.seal2}` : ''}` : 'Caja sin sellar (caja #1)'

  return (
    <Modal isOpen={target !== null} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-0'>
          <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Cajas de la recolección</span>
          <span className='text-base font-bold text-[#151930]'>{target.store}</span>
        </ModalHeader>
        <ModalBody className='gap-2'>
          {loading
            ? <div className='flex justify-center py-6'><Spinner size='sm' /></div>
            : rows.length === 0
              ? <p className='text-sm text-[#151930]/60 py-4'>Aún no hay etiquetas para esta recolección.</p>
              : rows.map((b) => {
                const url = resolveLabelUrl(b.pdf_url, idSeller)
                return (
                  <div key={b.id} className='flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2'>
                    <div className='flex flex-col'>
                      <span className='text-sm font-semibold text-[#151930]'>{sellosTxt(b)}</span>
                      <span className='text-[11px] text-[#151930]/60'>Tracking {b.tracking_number}</span>
                    </div>
                    {url !== null
                      ? <a href={url} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-sm font-medium text-[#37417f]'><MdDownload /> PDF</a>
                      : <span className='text-[11px] text-gray-400'>sin PDF</span>}
                  </div>
                )
              })}
        </ModalBody>
        <ModalFooter className='flex justify-between gap-2'>
          <Button
            variant='bordered'
            className='border-gray-200 text-[#151930]'
            isLoading={syncing}
            startContent={syncing ? undefined : <MdSync className='text-sm' />}
            onPress={() => { void sincronizar() }}
          >
            Sincronizar cajas
          </Button>
          <Button className='bg-[#151930] text-white font-semibold' onPress={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CajasModal
